/**
 * Which tiles to draw: a quadtree refined by screen-space error.
 *
 * ## Why screen-space error and not distance rings
 *
 * The obvious scheme — "z14 within 5 km, z13 within 15 km, …" — is wrong in two directions at
 * once. It over-draws when the camera is looking at the ground from 200 ft (everything is
 * close, so everything is maximum detail, including the tiles behind you) and under-draws from
 * 30,000 ft (everything is far, so the mountains you are actually looking at are mush). It also
 * has to be retuned for every field of view and every screen resolution.
 *
 * Screen-space error asks the only question that matters: *if I draw this tile at this level,
 * how many pixels will the error be?* A tile's geometric error is its vertex spacing in metres;
 * projected through the camera it becomes pixels, and a tile is refined while that exceeds the
 * tier's target. The result adapts to altitude, field of view, window size and quality tier
 * without a single hand-tuned distance in it, and the target — `geometryErrorPx` — is a number
 * with a meaning a human can reason about.
 *
 * ## Traversal
 *
 * Refinement starts from the root tiles at `minZoom` that fall inside the draw distance and
 * descends, so the visited set is proportional to what is actually drawn rather than to the
 * size of the planet. The stack is a preallocated `Int32Array`; a recursive traversal building
 * child objects would allocate hundreds of times per frame, which non-negotiable #3 forbids and
 * the allocation guard would catch.
 *
 * `updateFrame(alpha)`: called once per frame from the terrain system. Allocation-free after
 * construction.
 */

import { clamp } from '../../core/math.js';
import { horizonDistanceM } from '../geodesy.js';
import {
  MERCATOR_CIRCUMFERENCE_M,
  MERCATOR_MAX_LATITUDE_DEG,
  latitudeToTileYIndex,
  longitudeToTileXIndex,
  tileKey,
  tileSpanM,
  tilesPerAxis,
  wrapTileX,
} from './tileId.js';

export interface LodSelectorOptions {
  /** Shallowest zoom the traversal starts from. */
  minZoom?: number;
  /** Deepest zoom it will refine to; also bounded by the providers. */
  maxZoom?: number;
  /** Vertices along a tile edge — sets the geometric error at a given zoom. */
  gridSize?: number;
  /** Hard ceiling on selected tiles, so a pathological view cannot blow the draw-call budget. */
  maxTiles?: number;
  /** Ceiling on nodes visited per selection, as a runaway guard. */
  maxVisited?: number;
}

export interface LodSelectionParams {
  /** Camera position. */
  latitudeDeg: number;
  longitudeDeg: number;
  altitudeM: number;
  /** Drawing-buffer height [px] — screen-space error is measured against this. */
  viewportHeightPx: number;
  /** Vertical field of view [rad]. */
  verticalFovRad: number;
  /** Target screen-space error [px], from the quality tier. */
  geometryErrorPx: number;
  /** Far plane / terrain radius [m], from the quality tier. */
  maxDistanceM: number;
  /** Extra levels of refinement from the tier's `lodBias`. */
  lodBias: number;
}

export interface LodSelection {
  /** Packed tile keys, nearest first. */
  keys: number[];
  /** Distance from the camera to each tile [m], parallel to {@link keys}. */
  distancesM: number[];
  /**
   * Per-tile geomorph weight, 1 = this tile's own surface, 0 = its parent's.
   *
   * The LOD switch is where terrain visibly pops, and the vertex data to hide it has always been
   * there — every vertex carries `morphHeight`, the height it would have on the next coarser grid
   * (see `mesher.ts`). Nothing ever computed the weight, so the blend ran at a constant 1 and
   * every LOD change was a hard geometric snap.
   *
   * Derived here because this is where the decision is made and `errorPx` is already in hand. A
   * tile is drawn while its own screen-space error is under the threshold and its parent's — which
   * is twice its own — is over, so it lives in `errorPx ∈ (t/2, t]`. It reaches the far end of that
   * band exactly when the parent takes over, so the weight is driven to 0 there and the two
   * surfaces coincide at the moment of the swap. The parent, newly drawn, is at *its* near end and
   * so at 1 — which is what makes the handover invisible rather than merely smoother.
   */
  morphs: number[];
  /** Nodes visited during the traversal — a health figure for the overlay. */
  visited: number;
  /** True when {@link LodSelectorOptions.maxTiles} clipped the result. */
  clipped: boolean;
}

export class LodSelector {
  readonly minZoom: number;
  readonly maxZoom: number;
  readonly gridSize: number;
  /**
   * Hard ceiling on selected tiles. Mutable, because it is really the *tile cache's* budget
   * expressed in tiles, and the cache is resized whenever the quality tier changes — see
   * `TerrainSystem.applyQuality`.
   */
  maxTiles: number;
  readonly maxVisited: number;

  private readonly selection: LodSelection = {
    keys: [],
    distancesM: [],
    morphs: [],
    visited: 0,
    clipped: false,
  };

  /** Traversal stack of `(zoom, x, y)` triples. Sized for a full descent, well over-provisioned. */
  private readonly stack: Int32Array;
  private stackTop = 0;

  /**
   * The camera in normalised Mercator units (0…1 across the world), and the local scale that
   * turns those units into metres. Computed once per selection instead of per node — see
   * {@link distanceToTile}.
   */
  private cameraUnitX = 0;
  private cameraUnitY = 0;
  private metresPerUnit = MERCATOR_CIRCUMFERENCE_M;

  /**
   * Scratch for the nearest-first ordering, reused between frames.
   *
   * The scratch arrays and the hoisted comparator that used to live here are gone: the sort is
   * now an in-place heapsort over the selection's own two arrays, which needs no scratch and no
   * comparator. See {@link sortNearestFirst}.
   */

  constructor(options: LodSelectorOptions = {}) {
    this.minZoom = options.minZoom ?? 6;
    this.maxZoom = options.maxZoom ?? 14;
    this.gridSize = options.gridSize ?? 65;
    /*
     * The default is only for tests and for a selector built without a quality tier; the running
     * simulator overwrites it from the tier's tile-cache budget every time the tier changes.
     *
     * Why it has to come from there rather than being chosen: truncation drops the *farthest*
     * tiles, so a cap below what the view asks for does not coarsen the ground — it ends it,
     * somewhere out in the haze. That pushes toward a large number. But every selected tile is
     * necessarily resident, and the cache cannot evict a tile that is on screen, so the cap is
     * also a *floor* on cache occupancy. Raising it to 480 and measuring found 290 MB of resident
     * tiles against a 200 MB tier budget — the coverage the cap buys and the memory it costs are
     * the same number seen from two ends, and only one of them can be chosen freely.
     *
     * Measured demand at San Francisco, uncapped, on the High tier's 140 km draw distance: 477
     * tiles at 500 ft and 456 at 1,500 ft, falling to 279 by 16,000 ft. So the cap binds below
     * about 4,000 ft and nowhere else — above that the screen-space error threshold binds first
     * and the headroom is never taken.
     */
    this.maxTiles = options.maxTiles ?? 320;
    this.maxVisited = options.maxVisited ?? 20_000;
    this.stack = new Int32Array(this.maxVisited * 3);
  }

  /**
   * A tile's geometric error [m]: the spacing between its vertices.
   *
   * That is the largest height the mesh can be wrong by between samples, which is precisely
   * what refining halves.
   */
  geometricErrorM(zoom: number, latitudeDeg: number): number {
    return tileSpanM(zoom, latitudeDeg) / (this.gridSize - 1);
  }

  /** Geometric error using the cached local scale — the hot-loop form of the above. */
  private geometricErrorFast(zoom: number): number {
    return this.metresPerUnit / tilesPerAxis(zoom) / (this.gridSize - 1);
  }

  /**
   * Project a geometric error into pixels.
   *
   * `pixels = error · viewportHeight / (2 · distance · tan(fov/2))` — the standard perspective
   * relation. The distance is clamped away from zero because a tile the camera is sitting
   * inside would otherwise report infinite error and refine forever.
   */
  screenSpaceErrorPx(
    geometricErrorM: number,
    distanceM: number,
    viewportHeightPx: number,
    verticalFovRad: number,
  ): number {
    const d = Math.max(distanceM, 1);
    return (geometricErrorM * viewportHeightPx) / (2 * d * Math.tan(verticalFovRad / 2));
  }

  /**
   * Choose the tile set for this view.
   *
   * @returns the reused {@link LodSelection}. Read it before calling again.
   */
  select(params: LodSelectionParams): LodSelection {
    const selection = this.selection;
    selection.keys.length = 0;
    selection.distancesM.length = 0;
    selection.morphs.length = 0;
    selection.visited = 0;
    selection.clipped = false;

    // `lodBias` can only ever *coarsen*. A positive bias used to be allowed to push past
    // `maxZoom`, which asked the providers for zoom levels they have no data at — every one of
    // those tiles 404s and comes back as a flat placeholder, so the Ultra tier was quietly
    // replacing real mountains with sea-level plates. You cannot bias past the data.
    const deepestZoom = clamp(
      this.maxZoom + Math.round(params.lodBias),
      this.minZoom,
      this.maxZoom,
    );

    // Nothing beyond the geometric horizon can be seen, so nothing beyond it is worth
    // streaming. At 500 ft the horizon is 44 km and the tier's 80 km draw distance is pure
    // waste; at 30,000 ft it is 357 km and this does not bite at all. One slack tile so the
    // ground does not visibly end at the cull distance.
    const horizonM = horizonDistanceM(Math.max(0, params.altitudeM)) +
      tileSpanM(this.minZoom, params.latitudeDeg);
    const maxDistanceM = Math.min(params.maxDistanceM, horizonM);

    this.cacheCameraUnits(params);
    this.pushRoots(params, maxDistanceM);

    // Hoisted out of the loop: this is the same for every node, and it is two transcendentals.
    const pixelScale = params.viewportHeightPx / (2 * Math.tan(params.verticalFovRad / 2));

    while (this.stackTop > 0 && selection.visited < this.maxVisited) {
      this.stackTop -= 3;
      const zoom = this.stack[this.stackTop];
      const x = this.stack[this.stackTop + 1];
      const y = this.stack[this.stackTop + 2];
      selection.visited++;

      const distanceM = this.distanceToTile(zoom, x, y, params.altitudeM);
      if (distanceM > maxDistanceM) continue;

      const errorPx = (this.geometricErrorFast(zoom) * pixelScale) / Math.max(distanceM, 1);

      const refine = zoom < deepestZoom && errorPx > params.geometryErrorPx;
      if (!refine) {
        selection.keys.push(tileKey(zoom, x, y));
        selection.distancesM.push(distanceM);
        /*
         * Blend over the outer fifth of the band, not all of it.
         *
         * Morphing across the whole range would leave every tile permanently half-coarsened, which
         * is a real loss of detail everywhere to solve a problem that only exists at the boundary.
         * Full detail down to 0.7·t, then a ramp to the parent's surface by t/2, where the parent
         * takes over.
         */
        const threshold = params.geometryErrorPx;
        const morph = (errorPx - threshold * 0.5) / (threshold * 0.2);
        selection.morphs.push(morph < 0 ? 0 : morph > 1 ? 1 : morph);
        continue;
      }

      // Refine. Children are pushed unconditionally and range-culled when popped, which keeps
      // the culling logic in exactly one place.
      if (this.stackTop + 12 <= this.stack.length) {
        const childZoom = zoom + 1;
        const n = tilesPerAxis(childZoom);
        for (let dy = 0; dy < 2; dy++) {
          const childY = y * 2 + dy;
          if (childY < 0 || childY >= n) continue;
          for (let dx = 0; dx < 2; dx++) {
            this.stack[this.stackTop++] = childZoom;
            this.stack[this.stackTop++] = wrapTileX(x * 2 + dx, childZoom);
            this.stack[this.stackTop++] = childY;
          }
        }
      }
    }
    this.stackTop = 0;

    this.sortNearestFirst(selection);
    if (selection.keys.length > this.maxTiles) {
      selection.keys.length = this.maxTiles;
      selection.distancesM.length = this.maxTiles;
      selection.morphs.length = this.maxTiles;
      selection.clipped = true;
    }
    return selection;
  }

  /**
   * Seed the traversal with the root tiles within range.
   *
   * The root ring is computed in tile indices rather than by walking the whole world at
   * `minZoom`: at z6 the planet is 4096 tiles and visiting all of them every frame to reject
   * 4000 would be pure waste.
   */
  private pushRoots(params: LodSelectionParams, maxDistanceM: number): void {
    this.stackTop = 0;
    const zoom = this.minZoom;
    const n = tilesPerAxis(zoom);
    const spanM = tileSpanM(zoom, params.latitudeDeg);
    // How many root tiles the draw distance reaches, plus one for the tile we are standing in.
    const reach = Math.min(n, Math.ceil(maxDistanceM / Math.max(1, spanM)) + 1);

    const centreX = longitudeToTileXIndex(params.longitudeDeg, zoom);
    const centreY = latitudeToTileYIndex(params.latitudeDeg, zoom);

    for (let dy = -reach; dy <= reach; dy++) {
      const ty = centreY + dy;
      if (ty < 0 || ty >= n) continue; // no tiles past the Mercator cut-off
      for (let dx = -reach; dx <= reach; dx++) {
        if (this.stackTop + 3 > this.stack.length) return;
        this.stack[this.stackTop++] = zoom;
        this.stack[this.stackTop++] = wrapTileX(centreX + dx, zoom);
        this.stack[this.stackTop++] = ty;
      }
    }
  }

  /** Put the camera into normalised Mercator units, once per selection. */
  private cacheCameraUnits(params: LodSelectionParams): void {
    const lat = clamp(params.latitudeDeg, -MERCATOR_MAX_LATITUDE_DEG, MERCATOR_MAX_LATITUDE_DEG);
    const latRad = (lat * Math.PI) / 180;
    this.cameraUnitX = (params.longitudeDeg + 180) / 360;
    this.cameraUnitY = (1 - Math.asinh(Math.tan(latRad)) / Math.PI) / 2;
    this.metresPerUnit = MERCATOR_CIRCUMFERENCE_M * Math.cos(latRad);
  }

  /**
   * Distance from the camera to a tile [m], measured in Mercator units and scaled to metres.
   *
   * ## Why Mercator units rather than degrees
   *
   * The obvious implementation converts each tile's bounds to latitude and longitude and
   * measures on the sphere. That costs an `atan(sinh(·))` and a `cos` **per visited node**, and
   * the selector visits thousands of nodes every frame — measured, it was 2.1 ms a call, which
   * is the entire terrain streaming budget spent before a single tile has been fetched.
   *
   * In normalised Mercator units a tile is just `[x/n, (x+1)/n] × [y/n, (y+1)/n]`, so the
   * distance is pure arithmetic and the local scale factor is computed once for the camera.
   * Better still, Mercator is *conformal*: the same `cos(latitude)` scales the geometric error
   * and the distance alike, so it cancels out of their ratio and the LOD decision is exactly
   * right regardless of which latitude's scale factor was used. What remains approximate is
   * only the range cull at the draw distance, and only for tiles far enough north or south of
   * the camera for the scale to have drifted — where a slightly generous cull costs nothing.
   *
   * Altitude is folded in at the end, and it matters: at 30,000 ft the tile directly below is
   * 9 km away, not 0 m, and treating it as underfoot refines it to maximum detail for no
   * visible benefit at all.
   */
  private distanceToTile(zoom: number, x: number, y: number, altitudeM: number): number {
    const n = tilesPerAxis(zoom);
    const west = x / n;
    const east = (x + 1) / n;
    const north = y / n;
    const south = (y + 1) / n;

    let dx = 0;
    if (this.cameraUnitX < west) dx = west - this.cameraUnitX;
    else if (this.cameraUnitX > east) dx = this.cameraUnitX - east;
    if (dx > 0.5) dx = 1 - dx; // the short way round the antimeridian

    let dy = 0;
    if (this.cameraUnitY < north) dy = north - this.cameraUnitY;
    else if (this.cameraUnitY > south) dy = this.cameraUnitY - south;

    const horizontalM = Math.sqrt(dx * dx + dy * dy) * this.metresPerUnit;
    return Math.sqrt(horizontalM * horizontalM + altitudeM * altitudeM);
  }

  /**
   * Order the selection nearest-first, so the fetch queue's priorities are already right and
   * a clipped selection keeps the tiles the pilot is looking at.
   *
   * ## In-place, because `Array.prototype.sort` is not allocation-free
   *
   * This used to be an index sort — build an `order` array, `order.sort(comparator)`, then permute
   * two parallel arrays through it. That avoided the obvious garbage (no `{key, distance}` pairs)
   * and still allocated, because **`sort()` itself allocates**: V8's TimSort builds a `SortState`
   * and a work array sized to the element count on every call. Sorting ~330 tiles every frame is
   * therefore a few kilobytes a frame of guaranteed garbage no matter how carefully the caller
   * prepares its inputs.
   *
   * Heapsort instead: in place, O(n log n) worst case, and it touches nothing but the two arrays
   * it is given. It is not stable, which does not matter — ties are tiles at identical distance,
   * and their relative order has no meaning. It also removes the `order` indirection and the three
   * array copies that fed it, so the whole function is now two passes over the data.
   */
  private sortNearestFirst(selection: LodSelection): void {
    const keys = selection.keys;
    const distances = selection.distancesM;
    // Third parallel array, and it has to travel with the other two: a morph weight attached to
    // the wrong tile is not a crash, it is terrain that blends toward the wrong surface, which
    // looks like a subtler version of the popping this exists to remove.
    const morphs = selection.morphs;
    const count = keys.length;
    if (count < 2) return;

    // Build a max-heap, then repeatedly move the farthest tile to the end: ascending by distance.
    for (let start = (count >> 1) - 1; start >= 0; start--) {
      siftDown(keys, distances, morphs, start, count);
    }
    for (let end = count - 1; end > 0; end--) {
      swapPair(keys, distances, morphs, 0, end);
      siftDown(keys, distances, morphs, 0, end);
    }
  }
}

/** Swap entry `a` with entry `b` in all three parallel arrays. */
function swapPair(
  keys: number[],
  distances: number[],
  morphs: number[],
  a: number,
  b: number,
): void {
  const key = keys[a];
  keys[a] = keys[b];
  keys[b] = key;
  const distance = distances[a];
  distances[a] = distances[b];
  distances[b] = distance;
  const morph = morphs[a];
  morphs[a] = morphs[b];
  morphs[b] = morph;
}

/**
 * Restore the max-heap property at `root`, over the first `length` entries.
 *
 * Iterative rather than recursive: at a few hundred tiles the recursion is only eight or nine
 * deep, but a self-call here would sit on the once-per-frame terrain path and V8 does not inline
 * recursive calls, which is the whole reason this sort was rewritten.
 */
function siftDown(
  keys: number[],
  distances: number[],
  morphs: number[],
  root: number,
  length: number,
): void {
  let parent = root;
  for (;;) {
    const left = parent * 2 + 1;
    if (left >= length) return;
    const right = left + 1;
    let largest = left;
    if (right < length && distances[right] > distances[left]) largest = right;
    if (distances[largest] <= distances[parent]) return;
    swapPair(keys, distances, morphs, parent, largest);
    parent = largest;
  }
}

/**
 * Parent of a tile key, or the key itself at zoom 0.
 * Used to fall back to a coarser tile while a finer one is still streaming, so the ground is
 * never simply absent.
 */
export function parentKey(zoom: number, x: number, y: number): number {
  if (zoom === 0) return tileKey(0, 0, 0);
  return tileKey(zoom - 1, x >> 1, y >> 1);
}
